import { useRef, useState, useCallback, useEffect } from "react";

export type TDictationStatus = "idle" | "recording" | "transcribing" | "tidying" | "error" | "notice";

type TUseDictationProps = {
    apiUrl: string;
    caseId: string | null;
    sendTo: Array<string>;
    replyingToBody: string;
    replyingToFrom: string;
    onResult: (formattedBody: string) => void;
};

type TUseDictation = {
    status: TDictationStatus;
    message: string;
    canRetry: boolean;
    elapsedSeconds: number;
    processingSeconds: number;
    start: () => void;
    stop: () => void;
    retry: () => void;
    reset: () => void;
};

const MAX_RECORDING_SECONDS = 300;
const TRANSCRIBE_FETCH_TIMEOUT_MS = 150000;
const TIDY_FETCH_TIMEOUT_MS = 60000;

const RAW_TRANSCRIPT_FALLBACK_MESSAGE = "We couldn't tidy your dictation into an email. We've added the plain transcript instead.";

const pickSupportedMimeType = (): string => {
    const candidates = ["audio/webm", "audio/mp4", "audio/ogg"];
    const supported = candidates.find(
        (type) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)
    );
    return supported || "audio/webm";
};

const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = typeof reader.result === "string" ? reader.result : "";
            const comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });

const isAbortError = (e: unknown): boolean =>
    e instanceof DOMException && e.name === "AbortError";

const postJsonWithTimeout = <T>(url: string, body: unknown, timeoutMs: number): Promise<T> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", "accept": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
    })
        .then((res): Promise<T> => {
            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }
            return res.json();
        })
        .finally(() => clearTimeout(timeoutId));
};

export const useDictation = (props: TUseDictationProps): TUseDictation => {
    const [status, setStatus] = useState<TDictationStatus>("idle");
    const [message, setMessage] = useState("");
    const [canRetry, setCanRetry] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [processingSeconds, setProcessingSeconds] = useState(0);

    const propsRef = useRef(props);
    useEffect(() => {
        propsRef.current = props;
    });

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<Array<Blob>>([]);
    const mimeRef = useRef<string>("audio/webm");
    const audioBlobRef = useRef<Blob | null>(null);
    const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const processingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clearTimers = useCallback(() => {
        if (recordingTimerRef.current !== null) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        if (processingTimerRef.current !== null) {
            clearInterval(processingTimerRef.current);
            processingTimerRef.current = null;
        }
    }, []);

    const stopProcessingTimer = useCallback(() => {
        if (processingTimerRef.current !== null) {
            clearInterval(processingTimerRef.current);
            processingTimerRef.current = null;
        }
    }, []);

    const startProcessingTimer = useCallback(() => {
        stopProcessingTimer();
        setProcessingSeconds(0);
        processingTimerRef.current = setInterval(() => {
            setProcessingSeconds((prev) => prev + 1);
        }, 1000);
    }, [stopProcessingTimer]);

    const releaseStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        clearTimers();
        releaseStream();
        mediaRecorderRef.current = null;
        chunksRef.current = [];
        audioBlobRef.current = null;
        setElapsedSeconds(0);
        setProcessingSeconds(0);
        setMessage("");
        setCanRetry(false);
        setStatus("idle");
    }, [clearTimers, releaseStream]);

    const sendForTidy = useCallback((transcript: string) => {
        setStatus("tidying");

        const current = propsRef.current;
        postJsonWithTimeout<{ data?: { formatted_body: string } }>(
            `${current.apiUrl}/v1/email/ai-dictate-tidy`,
            {
                raw_transcript: transcript,
                case_id: current.caseId,
                send_to: current.sendTo,
                replying_to_body: current.replyingToBody,
                replying_to_from: current.replyingToFrom,
            },
            TIDY_FETCH_TIMEOUT_MS,
        )
            .then((json) => {
                stopProcessingTimer();
                audioBlobRef.current = null;
                const formattedBody = (json.data?.formatted_body || "").trim();
                if (formattedBody === "") {
                    propsRef.current.onResult(transcript);
                    setElapsedSeconds(0);
                    setMessage(RAW_TRANSCRIPT_FALLBACK_MESSAGE);
                    setStatus("notice");
                    return;
                }
                propsRef.current.onResult(formattedBody);
                setElapsedSeconds(0);
                setStatus("idle");
            })
            .catch(() => {
                stopProcessingTimer();
                audioBlobRef.current = null;
                propsRef.current.onResult(transcript);
                setElapsedSeconds(0);
                setMessage(RAW_TRANSCRIPT_FALLBACK_MESSAGE);
                setStatus("notice");
            });
    }, [stopProcessingTimer]);

    const sendForTranscription = useCallback((audioBlob: Blob) => {
        setStatus("transcribing");
        setMessage("");
        setCanRetry(false);
        startProcessingTimer();

        const current = propsRef.current;
        blobToBase64(audioBlob)
            .then((audioBase64) =>
                postJsonWithTimeout<{ data?: { raw_transcript: string } }>(
                    `${current.apiUrl}/v1/email/ai-transcribe`,
                    {
                        audio_base64: audioBase64,
                        mime_type: mimeRef.current,
                    },
                    TRANSCRIBE_FETCH_TIMEOUT_MS,
                )
            )
            .then((json) => {
                const transcript = (json.data?.raw_transcript || "").trim();
                if (transcript === "") {
                    stopProcessingTimer();
                    audioBlobRef.current = null;
                    setMessage("We didn't catch anything — try dictating again.");
                    setStatus("error");
                    return;
                }
                sendForTidy(transcript);
            })
            .catch((e: unknown) => {
                stopProcessingTimer();
                setMessage(
                    isAbortError(e)
                        ? "That took too long to process. Your recording is saved — try again."
                        : "We couldn't transcribe that just now. Your recording is saved — try again."
                );
                setCanRetry(true);
                setStatus("error");
            });
    }, [startProcessingTimer, stopProcessingTimer, sendForTidy]);

    const retry = useCallback(() => {
        const audioBlob = audioBlobRef.current;
        if (!audioBlob) {
            return;
        }
        sendForTranscription(audioBlob);
    }, [sendForTranscription]);

    const stop = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === "inactive") {
            return;
        }
        if (recordingTimerRef.current !== null) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        recorder.stop();
    }, []);

    const start = useCallback(() => {
        if (status === "recording" || status === "transcribing" || status === "tidying") {
            return;
        }
        setMessage("");
        setCanRetry(false);
        setElapsedSeconds(0);
        setProcessingSeconds(0);
        audioBlobRef.current = null;

        if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
            setMessage("Dictation isn't supported in this browser.");
            setStatus("error");
            return;
        }

        navigator.mediaDevices.getUserMedia({ audio: true })
            .then((stream) => {
                streamRef.current = stream;
                mimeRef.current = pickSupportedMimeType();
                const recorder = new MediaRecorder(stream, { mimeType: mimeRef.current });
                chunksRef.current = [];

                recorder.ondataavailable = (event: BlobEvent) => {
                    if (event.data && event.data.size > 0) {
                        chunksRef.current.push(event.data);
                    }
                };
                recorder.onstop = () => {
                    releaseStream();
                    const audioBlob = new Blob(chunksRef.current, { type: mimeRef.current });
                    chunksRef.current = [];
                    if (audioBlob.size === 0) {
                        setMessage("We didn't catch anything — try dictating again.");
                        setStatus("error");
                        return;
                    }
                    audioBlobRef.current = audioBlob;
                    sendForTranscription(audioBlob);
                };

                mediaRecorderRef.current = recorder;
                recorder.start();
                setStatus("recording");

                recordingTimerRef.current = setInterval(() => {
                    setElapsedSeconds((prev) => {
                        const next = prev + 1;
                        if (next >= MAX_RECORDING_SECONDS) {
                            stop();
                        }
                        return next;
                    });
                }, 1000);
            })
            .catch(() => {
                setMessage("We couldn't access your microphone. Check the browser's mic permission and try again.");
                setStatus("error");
            });
    }, [status, releaseStream, sendForTranscription, stop]);

    useEffect(
        () => () => {
            clearTimers();
            releaseStream();
        },
        [clearTimers, releaseStream]
    );

    return { status, message, canRetry, elapsedSeconds, processingSeconds, start, stop, retry, reset };
};
